import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from '@/lib/auth';
import i18n, { applyDocumentLanguage, normalizeLanguage, type SupportedLanguage } from './index';

interface LanguageContextValue {
  /** The language on screen right now. */
  language: SupportedLanguage;
  /** Put a different language on screen. Takes effect on the next render. */
  setLanguage: (language: SupportedLanguage) => void;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

/**
 * The one owner of the language on screen.
 *
 * The source of truth for a PERSON is `users.preferred_language`, the same
 * column Roldi's prompt and Whisper's transcription read server-side — so one
 * switch moves the interface, the assistant's replies and voice dictation
 * together instead of letting them drift apart. But the source of truth for the
 * CURRENT SCREEN is this component's state, because a technician's tap has to
 * land before the network does.
 *
 * It adopts the signed-in person's stored language on mount and again whenever a
 * DIFFERENT person signs in. The shop tablet swaps sessions IN PLACE
 * (`SelectTech` calls `setSession()` + `navigate()` with no reload), so "someone
 * else is signed in now" is not a page load — an effect keyed only to mount
 * would leave the previous tech's language up for the next one.
 *
 * What it deliberately does NOT do is re-assert the profile value on every
 * render. That is the bug this file used to have, and it made the switch look
 * broken (2026-09-02):
 *
 *   The effect depended on the `i18n` object from `useTranslation()`, and
 *   react-i18next v17 returns a FRESH wrapper object on every language change
 *   (`createI18nWrapper`, guarded by `wrapperLangRef.current !== lang`). So the
 *   effect re-ran on the very change the toggle had just made, compared the live
 *   language against the still-stale `user.preferredLanguage`, and reverted it —
 *   measured at 4 ms, before the browser painted. Nothing on screen moved until
 *   the save round-tripped: 885 ms warm, and however long a cold serverless
 *   function takes.
 *
 * Hence the singleton `i18n` imported from the module here, never the hook's
 * wrapper. This component WRITES the language; it does not read it, so it has no
 * business subscribing to it.
 */
export function LanguageProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const identity = user?.id ?? null;
  const profileLanguage = normalizeLanguage(user?.preferredLanguage);

  const [language, setLanguage] = useState<SupportedLanguage>(profileLanguage);

  // Read through a ref so the adopt-on-identity-change effect can name the
  // person as its only dependency. Depending on the language too would re-adopt
  // whenever the profile re-arrives — which is the revert, back again.
  const profileLanguageRef = useRef(profileLanguage);
  profileLanguageRef.current = profileLanguage;

  useEffect(() => {
    setLanguage(profileLanguageRef.current);
  }, [identity]);

  useEffect(() => {
    if (i18n.language !== language) void i18n.changeLanguage(language);
    // Not cosmetic: screen readers pick pronunciation from `lang`, so a Spanish
    // page left marked `en` is read aloud with English phonemes.
    applyDocumentLanguage(language);
  }, [language]);

  const value = useMemo<LanguageContextValue>(() => ({ language, setLanguage }), [language]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

/**
 * The language on screen, and the way to change it.
 *
 * Anything that needs to know what language the person is working in reads this
 * — not `user.preferredLanguage`, which lags a tap by a whole round trip and is
 * how the voice dictation and the header switch used to disagree.
 */
export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider.');
  return ctx;
}
